import { useState } from 'react';
import clsx from 'clsx';
import { RotateCcw } from 'lucide-react';
import { aud } from '@/utils/format';
import { rateCardTiers, SESSION_DURATION_MIN } from '@/domain/pricing';
import type { Artist, DayRateBasis, SessionLength } from '@/domain/types';

interface Props {
  artist: Artist | undefined;
  sessionLength: SessionLength;
  onSessionChange: (s: SessionLength) => void;

  dayRate: string;
  onDayRateChange: (v: string) => void;
  /** Hours driving a custom-built session; null while a tier card drives. */
  customHours: number | null;
  /** Step the hour builder — parent recalculates rate + deposit and clamps. */
  onCustomHoursChange: (h: number) => void;
  dayRateBasis: DayRateBasis;
  onDayRateBasisChange: (v: DayRateBasis) => void;
  deposit: string;
  onDepositChange: (v: string) => void;

  /** Standard day rate for the selected session (already incorporates any piece-plan override). */
  standardDayRate: number;
  /** Standard deposit for the selected session. */
  standardDeposit: number;
  /** True when the selected session's day rate comes from the piece's pricing plan. */
  usingPiecePlan?: boolean;

  /** Shown after a primary-artist change while a custom override is active. */
  showRateReset: boolean;
  onResetToStandard: () => void;

  idPrefix: string;
}

const BASIS_OPTIONS: Array<{ key: DayRateBasis; label: string }> = [
  { key: 'standard', label: 'Standard' },
  { key: 'gap-fill', label: 'Gap fill' },
  { key: 'friends-family', label: 'Friends & family' },
  { key: 'collab', label: 'Collab' },
  { key: 'apprentice', label: 'Apprentice' },
  { key: 'other', label: 'Other' },
];

const inputCls =
  'w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright';

/** Display hours rounded to one decimal (durationMin-derived values like 1.666… → "1.7"). */
const fmtHours = (h: number) => String(parseFloat(h.toFixed(1)));

/**
 * Session-length cards are the primary rate control: picking one sets the day
 * rate + deposit from the artist's rate card (or the piece plan). A collapsed
 * "Custom rate / deposit" section reveals the manual overrides + hour builder.
 */
export default function RateCardBlock({
  artist,
  sessionLength,
  onSessionChange,
  dayRate,
  onDayRateChange,
  customHours,
  onCustomHoursChange,
  dayRateBasis,
  onDayRateBasisChange,
  deposit,
  onDepositChange,
  standardDayRate,
  standardDeposit,
  usingPiecePlan,
  showRateReset,
  onResetToStandard,
  idPrefix,
}: Props) {
  const [customOpen, setCustomOpen] = useState(false);
  const tiers = rateCardTiers(artist);
  const overriding = dayRate !== '' && Math.round(Number(dayRate) || 0) !== standardDayRate;
  const depositOverriding = deposit !== '' && Math.round(Number(deposit) || 0) !== standardDeposit;

  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-widest text-cream-faint">
        {artist
          ? `Rates from ${artist.displayName}'s standard rate card`
          : 'Pick an artist to load their rate card'}
      </p>

      {/* Session length cards — the primary control */}
      <div className="mt-2 grid grid-cols-3 gap-2">
        {tiers.map((t) => (
          <button
            key={t.session}
            type="button"
            onClick={() => onSessionChange(t.session)}
            className={
              sessionLength === t.session
                ? 'rounded-md border border-gold-bright bg-gold/10 px-3 py-2 text-xs text-gold-bright text-left'
                : 'rounded-md border border-gold-dim/30 px-3 py-2 text-xs text-cream-soft hover:border-gold-bright/60 text-left'
            }
            data-testid={`${idPrefix}-session-${t.session}`}
          >
            <span className="block font-inter capitalize text-cream">
              {t.label}
              <span className="ml-1.5 font-mono text-[10px] uppercase tracking-widest text-cream-faint">
                {SESSION_DURATION_MIN[t.session] / 60}h
              </span>
            </span>
            <span className="block font-inter text-base text-gold-bright mt-1">{aud(t.dayRateAud)}</span>
            <span className="block font-mono text-[10px] uppercase tracking-widest text-cream-faint mt-0.5">
              deposit {aud(t.depositAud)}
            </span>
          </button>
        ))}
      </div>

      {/* Selected-tier summary line */}
      <div className="mt-2 flex items-center justify-between">
        <p className="font-serif text-[12px] text-cream-soft" data-testid={`${idPrefix}-rate-summary`}>
          {customHours != null ? (
            <span className="text-gold-bright">{fmtHours(customHours)}h session · </span>
          ) : null}
          Day rate <span className="text-cream">{aud(Math.round(Number(dayRate) || standardDayRate))}</span>
          {' · deposit '}
          <span className="text-cream">{aud(Math.round(Number(deposit) || standardDeposit))}</span>
          {usingPiecePlan ? (
            <span className="ml-1.5 rounded-full border border-gold-dim/40 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-gold-bright">
              From piece plan
            </span>
          ) : null}
        </p>
      </div>

      {/* Reset prompt after an artist change with an active override */}
      {showRateReset ? (
        <div
          className="mt-2 flex items-center justify-between gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5"
          data-testid={`${idPrefix}-rate-reset`}
        >
          <span className="text-[12px] text-amber-300">Rates changed — reset to standard?</span>
          <button
            type="button"
            onClick={onResetToStandard}
            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-amber-200 hover:text-cream"
          >
            <RotateCcw size={11} /> Reset
          </button>
        </div>
      ) : null}

      {/* Custom rate / deposit (collapsed) */}
      <button
        type="button"
        onClick={() => setCustomOpen((v) => !v)}
        className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-gold-bright/60 px-3 py-1.5 text-xs font-medium text-gold-bright hover:bg-gold/10 transition"
        data-testid={`${idPrefix}-custom-toggle`}
        aria-expanded={customOpen}
      >
        {customOpen ? '− ' : '+ '}Custom rate / deposit
      </button>

      {customOpen ? (
        <div className="mt-2 space-y-3 rounded-md border border-gold-dim/30 p-3" data-testid={`${idPrefix}-custom`}>
          {/* Build from hours — rate prices from the artist's hourly rate */}
          <div>
            <div className="flex items-center justify-between">
              <span className="label-small-caps">Build from hours</span>
              <div className="inline-flex items-center gap-1">
                <button
                  type="button"
                  aria-label="Decrease hours"
                  onClick={() => onCustomHoursChange((customHours ?? SESSION_DURATION_MIN[sessionLength] / 60) - 0.5)}
                  disabled={(customHours ?? SESSION_DURATION_MIN[sessionLength] / 60) <= 0.5}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-gold-dim/40 text-cream-soft hover:border-gold-bright/60 hover:text-cream transition disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid={`${idPrefix}-hours-minus`}
                >
                  −
                </button>
                <span
                  className="w-12 text-center font-inter text-sm text-cream"
                  data-testid={`${idPrefix}-hours-value`}
                >
                  {fmtHours(customHours ?? SESSION_DURATION_MIN[sessionLength] / 60)}h
                </span>
                <button
                  type="button"
                  aria-label="Increase hours"
                  onClick={() => onCustomHoursChange((customHours ?? SESSION_DURATION_MIN[sessionLength] / 60) + 0.5)}
                  disabled={(customHours ?? SESSION_DURATION_MIN[sessionLength] / 60) >= 10}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-gold-dim/40 text-cream-soft hover:border-gold-bright/60 hover:text-cream transition disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid={`${idPrefix}-hours-plus`}
                >
                  +
                </button>
              </div>
            </div>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-cream-faint" data-testid={`${idPrefix}-hours-working`}>
              {customHours != null
                ? `${fmtHours(customHours)}h × ${aud(artist?.rates.hourlyAud ?? 0)}/h = ${aud(Math.round(customHours * (artist?.rates.hourlyAud ?? 0)))}`
                : artist
                  ? `Prices from ${artist.displayName}'s ${aud(artist.rates.hourlyAud)}/h rate · sets the calendar duration`
                  : 'Pick an artist to price from their hourly rate'}
            </p>

            {/* Day rate — editable; typing overrides the hours calculation */}
            <div className="mt-3">
              <span className="label-small-caps">Day rate</span>
              <input
                type="number"
                min={0}
                value={dayRate}
                onChange={(e) => onDayRateChange(e.target.value)}
                placeholder={String(standardDayRate)}
                className={clsx(inputCls, 'mt-2')}
                data-testid={`${idPrefix}-dayrate`}
              />
              {overriding ? (
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-cream-faint">
                  Standard {aud(standardDayRate)}
                </p>
              ) : null}
            </div>
          </div>

          {/* Basis */}
          <label className="block">
            <span className="label-small-caps">Rate basis</span>
            <select
              value={dayRateBasis}
              onChange={(e) => onDayRateBasisChange(e.target.value as DayRateBasis)}
              className={clsx(inputCls, 'mt-2')}
              data-testid={`${idPrefix}-dayrate-basis`}
            >
              {BASIS_OPTIONS.map((b) => (
                <option key={b.key} value={b.key}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>

          {/* Deposit override */}
          <label className="block">
            <span className="label-small-caps">Deposit (AUD)</span>
            <input
              type="number"
              min={0}
              value={deposit}
              onChange={(e) => onDepositChange(e.target.value)}
              placeholder={String(standardDeposit)}
              className={clsx(inputCls, 'mt-2')}
              data-testid={`${idPrefix}-deposit-amount`}
            />
            {depositOverriding ? (
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-cream-faint">
                Standard {aud(standardDeposit)} · $0 = hold with no deposit
              </p>
            ) : null}
          </label>
        </div>
      ) : null}
    </div>
  );
}
